import type { JSX } from "preact";
import { useState } from "preact/hooks";

import { type Session, api, can } from "../api";
import { Button, Card, Empty, ErrorNote, Loading, Pill, Stat } from "../components/Ui";
import { datetime, huf } from "../format";
import { useApi } from "../useApi";

interface PriceChange {
  kod: string;
  megnevezes: string;
  elotte: number;
  utana: number;
}

interface Diff {
  against: number | null;
  azonos: boolean;
  valtozas: {
    summary?: string;
    price_changes?: PriceChange[];
    added_items?: string[];
    removed_items?: string[];
    alias_delta?: number;
    rule_delta?: number;
  };
}

interface Version {
  id: number;
  created_at: string;
  activated_at: string | null;
  active: boolean;
  source: string;
  item_count: number;
  alias_count: number;
  rule_count: number;
  created_by: string;
  note: string | null;
}

interface PricelistFile {
  fajl: string;
  formatum: string;
  szerep: string;
  munkalap: string;
  sorok: number;
}

interface PricelistSource {
  fajlok: PricelistFile[];
  ellenorizve: string | null;
  verzio?: number | null;
}

const ROLE_LABEL: Record<string, string> = {
  arlista: "árlista",
  csomagok: "csomaglista",
  beallitasok: "beállítások",
};

interface CellReading {
  tetel: string;
  tevesen: string[];
  reszei: string[];
  leirasok: string[];
}

interface PricelistCheck {
  ellenorizve: string | null;
  verzio?: number | null;
  szamok?: {
    tetel: number;
    egyeb_nev: number;
    tevesen: number;
    resze: number;
    leiras: number;
    kerdes: number;
  };
  ertelmezes?: CellReading[];
  kerdesek?: { kifejezes: string; lehetosegek: string[] }[];
  figyelmeztetesek?: string[];
  atvilagitas?: {
    probak: number;
    hiba: number;
    figyelem: number;
    talalatok: { szoveg: string; leiras: string }[];
  };
}

/**
 * Az árlista ellenőrzése.
 *
 * Két dolgot mutat meg, amit a rendelő máshonnan nem tudhat:
 *
 * 1. **Ad-e a rendszer bármire másik tételt, mint amit kértek.** Minden új
 *    árlista-verzión lefut egy átvilágítás, ami a nevek és egyéb nevek
 *    több ezer írásmódjára végigpróbálja a párosítást.
 * 2. **Hogyan értette a rendszer az „egyéb nevei” cellákat.** A cella szabad
 *    szöveg; itt látszik, mit vett tévesen használt névnek, mit résznek, és
 *    mit megjegyzésnek — ha a rendelő másképp gondolta, a cellát írja át.
 */
function CheckCard({ reloadKey }: { reloadKey: number }): JSX.Element | null {
  const { data } = useApi<PricelistCheck>("/admin/v1/rules/check", [reloadKey]);
  if (!data) return null;
  if (!data.ellenorizve || !data.atvilagitas || !data.szamok) {
    return (
      <Card title="Árlista ellenőrzés">
        <Empty>
          Ehhez az árlistához még nem készült ellenőrzés. A következő szinkronnál elkészül.
        </Empty>
      </Card>
    );
  }
  const { atvilagitas, szamok } = data;
  const ertelmezes = data.ertelmezes ?? [];
  const kerdesek = data.kerdesek ?? [];
  const figyelmeztetesek = data.figyelmeztetesek ?? [];
  return (
    <Card
      title="Árlista ellenőrzés"
      hint="Minden új árlista-verzión lefut. A megnevezések több ezer írásmódjára ellenőrzi, hogy a rendszer a kért tételt adja-e."
    >
      {atvilagitas.hiba === 0 ? (
        <p className="notice notice--ok">
          <strong>Rendben.</strong> {atvilagitas.probak.toLocaleString("hu-HU")} próbából egyszer
          sem adott a rendszer másik tételt, mint amit kértek.
        </p>
      ) : (
        <>
          <p className="notice notice--bad">
            <strong>{atvilagitas.hiba} megnevezésre</strong> a rendszer megerősítés nélkül másik
            tételt adna. Kérjük, jelezze a fejlesztőnek.
          </p>
          <ul className="plain">
            {atvilagitas.talalatok.slice(0, 10).map((t) => (
              <li key={t.szoveg}>
                <strong>{t.szoveg}</strong> — <span className="muted">{t.leiras}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="stats">
        <Stat label="tétel" value={String(szamok.tetel)} />
        <Stat label="egyéb név" value={String(szamok.egyeb_nev)} />
        <Stat label="visszakérdezés" value={String(szamok.kerdes)} />
        <Stat label="megjegyzésnek vett szöveg" value={String(szamok.leiras)} />
      </div>

      {figyelmeztetesek.length > 0 && (
        <details>
          <summary>
            <strong>{figyelmeztetesek.length} figyelmeztetés</strong> az árlista adataihoz
          </summary>
          <ul className="plain">
            {figyelmeztetesek.map((f, i) => (
              <li key={i} className="muted">
                {f}
              </li>
            ))}
          </ul>
        </details>
      )}

      {ertelmezes.length > 0 && (
        <details>
          <summary>
            <strong>Az „egyéb nevei” cellák értelmezése</strong> — {szamok.tevesen} tévesen használt
            név, {szamok.resze} rész, {szamok.leiras} megjegyzés
          </summary>
          <p className="muted">
            A <em>tévesen használt név</em> és a <em>rész</em> ugyanúgy talál, mint bármely más név.
            A <em>megjegyzés</em> nem név: arra a rendszer nem ad árat magától, csak megerősítéssel
            kínálja fel a tételt. Ha egy megjegyzésnek vett szöveget névnek szánt, írja a cellába
            címke és mondat nélkül, vesszővel elválasztva.
          </p>
          <table className="rows">
            <thead>
              <tr>
                <th>Tétel</th>
                <th>Tévesen használt név</th>
                <th>Részei</th>
                <th>Megjegyzésnek vett szöveg</th>
              </tr>
            </thead>
            <tbody>
              {ertelmezes.map((e) => (
                <tr key={e.tetel}>
                  <td>
                    <strong>{e.tetel}</strong>
                  </td>
                  <td>{e.tevesen.join(", ")}</td>
                  <td>{e.reszei.join(", ")}</td>
                  <td className="muted">{e.leirasok.join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {kerdesek.length > 0 && (
        <details>
          <summary>
            <strong>{kerdesek.length} megnevezés több tételnél is szerepel</strong> — ezekre a
            rendszer visszakérdez
          </summary>
          <p className="muted">
            Ha egy megnevezés csak az egyik tételt jelenti, törölje a másik tétel „egyéb nevei”
            cellájából — onnantól a rendszer nem kérdez, hanem árat ad.
          </p>
          <table className="rows">
            <thead>
              <tr>
                <th>Megnevezés</th>
                <th>Amelyik tételeknél szerepel</th>
              </tr>
            </thead>
            <tbody>
              {kerdesek.map((k) => (
                <tr key={k.kifejezes}>
                  <td>
                    <strong>{k.kifejezes}</strong>
                  </td>
                  <td className="muted">{k.lehetosegek.join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      <p className="muted">
        Utoljára ellenőrizve: {datetime(data.ellenorizve)}
        {data.verzio ? ` · #${data.verzio} verzió` : ""}
      </p>
    </Card>
  );
}

/**
 * Melyik fájlból jön az árlista.
 *
 * A felismerés automatikus: a rendszer a táblázat TARTALMÁBÓL dönti el, hogy
 * árlista-e, a fájl nevétől és formátumától függetlenül. Ezért kell itt
 * látszania — egy csere (Excel → Google Táblázat) után innen ellenőrizhető,
 * hogy a jó fájlt olvassuk.
 */
function SourceCard({ reloadKey }: { reloadKey: number }): JSX.Element | null {
  const { data } = useApi<PricelistSource>("/admin/v1/rules/source", [reloadKey]);
  if (!data) return null;
  const fajlok = data.fajlok ?? [];
  return (
    <Card
      title="Az árlista forrása"
      hint="A rendszer a táblázat tartalmából ismeri fel az árlistát — a fájl neve és formátuma nem számít."
    >
      {fajlok.length === 0 ? (
        <Empty>
          Még nem volt szinkron ezzel a verzióval. Indítson szinkront, és itt megjelenik, melyik
          fájlt ismerte fel a rendszer.
        </Empty>
      ) : (
        <>
          <table className="rows">
            <thead>
              <tr>
                <th>Fájl</th>
                <th>Felismerve mint</th>
                <th>Formátum</th>
                <th>Munkalap</th>
                <th className="num">Sorok</th>
              </tr>
            </thead>
            <tbody>
              {fajlok.map((f) => (
                <tr key={`${f.fajl}:${f.munkalap}`}>
                  <td>
                    <strong>{f.fajl}</strong>
                  </td>
                  <td>
                    <Pill tone="ok">{ROLE_LABEL[f.szerep] ?? f.szerep}</Pill>
                  </td>
                  <td>{f.formatum}</td>
                  <td className="muted">{f.munkalap}</td>
                  <td className="num">{f.sorok}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted">Utoljára ellenőrizve: {datetime(data.ellenorizve)}</p>
        </>
      )}
    </Card>
  );
}

/**
 * Szabályverziók.
 *
 * **A legsúlyosabb következményű felület**: egy aktiválás megváltoztatja
 * az árakat. Ezért csak adminisztrátor teheti, minden lépés naplózódik,
 * és a korábbi verziók megmaradnak — a visszaállás egy kattintás.
 */
export function RulesView({ session }: { session: Session | null }): JSX.Element {
  const { data, error, loading, reload } = useApi<Version[]>("/admin/v1/rules/versions");
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nyitott, setNyitott] = useState<number | null>(null);
  const [sourceKey, setSourceKey] = useState(0);

  const run = async (fn: () => Promise<unknown>, siker: string): Promise<void> => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      setMessage(siker);
      reload();
      setSourceKey((k) => k + 1);
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (loading && !data) return <Loading />;

  return (
    <>
    <SourceCard reloadKey={sourceKey} />
<CheckCard reloadKey={sourceKey} />
    <Card
      title="Szabályverziók"
      hint="Minden szinkron új verziót hoz létre. A régiek megmaradnak, a visszaállás
            egy kattintás."
      actions={
        can(session, "szinkronizal") && (
          <Button
            onClick={() =>
              run(() => api.post("/admin/v1/rules/sync"), "A szinkron lefutott.")
            }
            disabled={busy}
          >
            {busy ? "Fut…" : "Szinkron indítása"}
          </Button>
        )
      }
    >
      {message && <p className="notice notice--info">{message}</p>}

      {!data?.length ? (
        <Empty>
          Még nincs szabályverzió. Indítson szinkront — enélkül az árazás nem működik.
        </Empty>
      ) : (
        <table className="rows">
          <thead>
            <tr>
              <th>Verzió</th>
              <th>Készült</th>
              <th>Forrás</th>
              <th className="num">Tétel</th>
              <th className="num">Alias</th>
              <th className="num">Szabály</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.flatMap((v) => [
              <tr key={v.id} className={v.active ? "is-active" : ""}>
                <td>
                  <strong>#{v.id}</strong> {v.active && <Pill tone="ok">aktív</Pill>}
                </td>
                <td title={datetime(v.created_at)}>{datetime(v.created_at)}</td>
                <td className="muted">
                  {v.source}
                  {v.created_by && <span> · {v.created_by}</span>}
                </td>
                <td className="num">{v.item_count}</td>
                <td className="num">{v.alias_count}</td>
                <td className="num">{v.rule_count}</td>
                <td className="num">
                  {!v.active && (
                    <Button onClick={() => setNyitott(nyitott === v.id ? null : v.id)}>
                      {nyitott === v.id ? "Elrejt" : "Mi változna?"}
                    </Button>
                  )}
                  {!v.active && can(session, "szabalyt_aktival") && (
                    <Button
                      onClick={() =>
                        run(
                          () =>
                            api.post(`/admin/v1/rules/versions/${v.id}/activate`, {
                              note: "adminból aktiválva",
                            }),
                          `A #${v.id} verzió aktív.`,
                        )
                      }
                      disabled={busy}
                    >
                      Aktiválás
                    </Button>
                  )}
                </td>
              </tr>,
              nyitott === v.id ? (
                <tr key={`${v.id}-diff`}>
                  <td colSpan={7}>
                    <DiffPanel versionId={v.id} />
                  </td>
                </tr>
              ) : null,
            ])}
          </tbody>
        </table>
      )}

      {!can(session, "szabalyt_aktival") && (
        <p className="muted">
          A verzió aktiválása adminisztrátori jog — ez a művelet megváltoztatja az
          árakat.
        </p>
      )}
    </Card>
    </>
  );
}

/**
 * „Mi változna, ha ezt aktiválnám?”
 *
 * **Aktiválás előtt ez a legfontosabb kérdés.** Az aktiválás azonnal
 * megváltoztatja az árakat minden további árajánlatban; egy téves
 * szinkron addig marad élesben, amíg valaki észre nem veszi. Az
 * árváltozások listája ezt teszi láthatóvá — a puszta „467 tétel” nem
 * mondja meg, hogy közben egy vizsgálat ára megtízszereződött-e.
 */
function DiffPanel({ versionId }: { versionId: number }): JSX.Element {
  const { data, error, loading } = useApi<Diff>(`/admin/v1/rules/versions/${versionId}/diff`);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (loading && !data) return <Loading />;
  if (!data) return <></>;

  if (data.azonos) {
    return <Empty>Ez az aktív verzió — nincs mihez hasonlítani.</Empty>;
  }

  const v = data.valtozas;
  const arak = v.price_changes ?? [];
  const uj = v.added_items ?? [];
  const torolt = v.removed_items ?? [];

  return (
    <div className="diff">
      <p className="muted">
        Összevetve az aktív {data.against ? `#${data.against}` : "—"} verzióval.
        {v.summary && <> {v.summary}</>}
      </p>

      <div className="stats">
        <Stat label="Árváltozás" value={String(arak.length)} tone={arak.length ? "warn" : ""} />
        <Stat label="Új tétel" value={String(uj.length)} />
        <Stat label="Eltűnt tétel" value={String(torolt.length)} tone={torolt.length ? "warn" : ""} />
        <Stat label="Alias" value={fmtDelta(v.alias_delta)} />
      </div>

      {arak.length > 0 && (
        <>
          <h3>Árváltozások</h3>
          <table className="rows">
            <thead>
              <tr>
                <th>Vizsgálat</th>
                <th className="num">Eddig</th>
                <th className="num">Ezután</th>
                <th className="num">Eltérés</th>
              </tr>
            </thead>
            <tbody>
              {arak.map((c) => {
                const kulonbseg = c.utana - c.elotte;
                return (
                  <tr key={c.kod}>
                    <td>
                      {c.megnevezes}
                      <div className="mono muted">{c.kod}</div>
                    </td>
                    <td className="num">{huf(c.elotte)}</td>
                    <td className="num">
                      <strong>{huf(c.utana)}</strong>
                    </td>
                    <td className="num">
                      <Pill tone={kulonbseg > 0 ? "warn" : "info"}>
                        {kulonbseg > 0 ? "+" : ""}
                        {huf(kulonbseg)}
                      </Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {torolt.length > 0 && (
        <>
          <h3>Eltűnt tételek</h3>
          <p className="muted">
            Ezekre a kódokra mutató aliasok és szabályok elveszítik a kapcsolatot.
            Gyakori ok: a tétel <strong>átnevezése</strong> az árlistán — az új név új
            kódot jelent.
          </p>
          <p className="mono">{torolt.join(", ")}</p>
        </>
      )}

      {arak.length === 0 && uj.length === 0 && torolt.length === 0 && (
        <Empty>Az árakat és a tételkészletet nem érinti — csak aliasok vagy szabályok változtak.</Empty>
      )}
    </div>
  );
}

function fmtDelta(value: number | undefined): string {
  if (value == null) return "—";
  return value > 0 ? `+${value}` : String(value);
}
